import axios from 'axios';
import _ from 'lodash';
import { setSegmentModelMetadata } from '@foxcorp/delta-web-analytics';
import { helperGetApiHeaders, helperConvertPathToApiRoute } from 'helpers/ApiHelper';
import { PAGE_COLLECTION } from 'helpers/PageHelper';
import {
  setVideoPanel as setGlobalVideoPanel,
  clearVideo as clearGlobalVideo,
} from 'components/VideoPlayerGlobal/Actions';
import { concatToUrl, isStaticRender } from 'helpers/ContentHelper';
import { replaceScreenUrl as optimizelyReplaceCollections } from 'helpers/OptimizelyHelper';
import { autoplayEnabled } from 'helpers/ConfigHelper';
import { getDmaFromVideo } from 'helpers/AuthHelper';
import { fetchFooter } from 'components/Footer/FooterActions';
import { getCurrentShowcase, setShowcaseVideoDma } from './Helpers';

export const CLEAR_COLLECTION_PANELS = 'CLEAR_COLLECTION_PANELS';
export const RESET_PANEL_LOADED = 'RESET_PANEL_LOADED';
export const FETCH_COLLECTION = 'FETCH_COLLECTION';
export const FETCH_COLLECTION_PANEL = 'FETCH_COLLECTION_PANEL';
export const SET_COLLECTION = 'SET_COLLECTION';
export const SET_COLLECTION_USE_SHOWCASE_FALLBACK = 'SET_COLLECTION_USE_SHOWCASE_FALLBACK';
export const SET_COLLECTION_FORCE_KEYART_FOR_SHOWCASE = 'SET_COLLECTION_FORCE_KEYART_FOR_SHOWCASE';
export const SET_USER_BOOKMARKS = 'SET_USER_BOOKMARKS';
export const UPDATE_VISIBLE_ELEMENTS = 'UPDATE_VISIBLE_ELEMENTS';
export const UPDATE_COLLECTION_ORDER = 'UPDATE_COLLECTION_ORDER';
export const UPDATE_SHOWCASE_VIDEO = 'UPDATE_SHOWCASE_VIDEO';
export const INCREMENT_PANEL_RENDER_COUNT = 'INCREMENT_PANEL_RENDER_COUNT';
export const RESET_PANEL_RENDER_COUNT = 'RESET_PANEL_RENDER_COUNT';

async function fetchEventsFromCollections(collection, accessToken, deviceId) {
  const data = collection?.data;
  const fetchedWithEventData = data?.panels?.member?.[0]?.items?.member?.map(async item => {
    const linkedEvent = item?.houseIDLinked?.find(linkedId => linkedId?.['@type'] === 'Event');
    const linkedId = linkedEvent?.['@id'];
    const event =
      linkedId && (await axios.get(linkedId, helperGetApiHeaders({ accessToken, deviceId })));
    return { ...item, eventData: event?.data };
  });
  if (fetchedWithEventData) {
    _.set(data, 'panels.member.0.items.member', (await Promise.all(fetchedWithEventData)) || []);
  }
  return { ...collection, data };
}

function fetchCollection({ account, accessToken, deviceId, path, screenName }) {
  const pageType = 'home';
  const replaceCollectionScreen = optimizelyReplaceCollections(pageType, screenName);
  const isPersonalizedCollection = path === 'screens/home' && account;
  const collectionPath = `${replaceCollectionScreen || path}${
    isPersonalizedCollection ? '?personalized=true' : ''
  }`;
  const request = axios
    .get(
      concatToUrl(helperConvertPathToApiRoute(collectionPath)),
      helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId }),
    )
    .then(result => fetchEventsFromCollections(result, accessToken, deviceId));
  return {
    type: FETCH_COLLECTION,
    payload: request,
  };
}

export function setUserBookmarks({ profileBookmarks }) {
  return async dispatch => {
    const bookmarks = await profileBookmarks.get();

    dispatch({
      type: SET_USER_BOOKMARKS,
      payload: { bookmarks },
    });
  };
}

function setCollection(collection, initialShowcase) {
  return {
    type: SET_COLLECTION,
    payload: {
      collection,
      initialShowcase,
    },
  };
}

export function clearCollectionPanels() {
  return { type: CLEAR_COLLECTION_PANELS };
}

export function setUseShowcaseFallback(payload) {
  return { type: SET_COLLECTION_USE_SHOWCASE_FALLBACK, payload };
}

export function fetchCollectionPanel(panel, accessToken, deviceId) {
  const { '@id': url } = panel;
  const request = axios.get(url, helperGetApiHeaders({ accessToken, deviceId }));
  return {
    type: FETCH_COLLECTION_PANEL,
    payload: request,
  };
}

export function fetchCollections({
  accessToken,
  account,
  deviceId,
  panelsToPrefetch,
  path,
  screenName,
}) {
  return (dispatch, getState) => {
    let collection;
    return dispatch(fetchCollection({ path, accessToken, screenName, deviceId, account })).then(
      result => {
        const experimentData = _.get(result, 'payload.data.trackingData.properties.abtest', null);
        const experimentKey = _.get(experimentData, 'experimentKey', null);
        const variationKey = _.get(experimentData, 'variationKey', null);
        const dma = getDmaFromVideo(_.get(result, 'payload.data'));

        setSegmentModelMetadata(
          result?.payload?.data?.trackingData?.properties?.modelMetaData ?? {},
        );

        collection = _.get(result, 'payload.data.panels', {});
        collection.seo = _.get(result, 'payload.data.SEO', null);
        collection.categoryType = result?.payload?.data?.categoryType;
        if (experimentKey && variationKey)
          collection.experimentData = `${experimentKey}:${variationKey}`;
        const screenPanels = _.get(collection, 'member');
        if (!screenPanels) {
          return Promise.resolve();
        }
        return Promise.all(
          screenPanels.map((screenPanel, index) => {
            if (!screenPanel) return Promise.resolve();
            const { items, member, types } = screenPanel;
            const panelIsNotPopulated = !items && !member && !types;
            const prefetchPanel = panelIsNotPopulated && index < panelsToPrefetch;
            if (prefetchPanel) {
              return dispatch(fetchCollectionPanel(screenPanel, accessToken, deviceId)).then(
                item => {
                  const data = _.get(item, 'payload.data');
                  _.set(collection, `member.${index}`, data);
                  return Promise.resolve();
                },
              );
            }
            return Promise.resolve();
          }),
        )
          .then(() => {
            const showcase = getCurrentShowcase(collection.member);
            if (!isStaticRender()) {
              const newVideoId = _.get(showcase, 'items.member.0.id');
              const oldVideoId = _.get(getState(), 'globalVideo.panelVideo.id');
              const videoChanged = newVideoId && newVideoId !== oldVideoId;
              const shouldSetGlobalVideo = autoplayEnabled() && videoChanged;
              if (shouldSetGlobalVideo) {
                const panel = setShowcaseVideoDma({ showcase, dma });
                const source = PAGE_COLLECTION;
                dispatch(setGlobalVideoPanel({ panel, source }));
              }
            }
            return dispatch(setCollection(collection, showcase));
          })
          .then(() => dispatch(fetchFooter()));
      },
    );
  };
}

export function fetchCollectionsOnHeaderClick({
  accessToken,
  account,
  deviceId,
  panelsToPrefetch,
  path,
  screenName,
}) {
  return dispatch => {
    if (autoplayEnabled()) {
      dispatch(clearGlobalVideo({ source: PAGE_COLLECTION }));
    }
    dispatch(clearCollectionPanels());
    return dispatch(
      fetchCollections({
        accessToken,
        account,
        deviceId,
        panelsToPrefetch,
        path,
        screenName,
      }),
    );
  };
}

export function updateVisibleElements(currentElements) {
  return {
    type: UPDATE_VISIBLE_ELEMENTS,
    payload: currentElements,
  };
}

export function updateCollectionOrder(panel) {
  const id = _.get(panel, 'id', null);
  return {
    type: UPDATE_COLLECTION_ORDER,
    payload: id,
  };
}

export function forceKeyartForShowcase(value) {
  return {
    type: SET_COLLECTION_FORCE_KEYART_FOR_SHOWCASE,
    payload: value,
  };
}

export function updateShowcaseVideo(video) {
  return {
    type: UPDATE_SHOWCASE_VIDEO,
    payload: video,
  };
}

export function incrementPanelRenderCount() {
  return {
    type: INCREMENT_PANEL_RENDER_COUNT,
  };
}

export function resetPanelRenderCount() {
  return {
    type: RESET_PANEL_RENDER_COUNT,
  };
}

export function resetPanelLoaded() {
  return {
    type: RESET_PANEL_LOADED,
  };
}
